/**
 * @fileoverview Modal component
 *
 * JavaScript modal plugin to add dialogs with custom content.
 */

import SelectorEngine from '../../utils/dom/selector-engine';
import Manipulator from '../../utils/dom/manipulator';
import EventHandler from '../../utils/dom/event-handler';
import { reflow, getTransitionDurationFromElement, emulateTransitionEnd, TRANSITION_END } from '../../utils/utils';
import BaseComponent from '../BaseComponent';

const defaultConfig = {
	backdrop: true,
	keyboard: true,
	focus: true
};

const EVENT_KEY = `.lta.modal`;
const ESCAPE_KEY = 'Escape';

export const EVENT_HIDE = `hide${EVENT_KEY}`;
const EVENT_HIDE_PREVENTED = `hidePrevented${EVENT_KEY}`;
const EVENT_HIDDEN = `hidden${EVENT_KEY}`;
const EVENT_SHOW = `show${EVENT_KEY}`;
const EVENT_SHOWN = `shown${EVENT_KEY}`;
const EVENT_FOCUSIN = `focusin${EVENT_KEY}`;
const EVENT_RESIZE = `resize${EVENT_KEY}`;
const EVENT_CLICK_DISMISS = `click.dismiss${EVENT_KEY}`;
const EVENT_KEYDOWN_DISMISS = `keydown.dismiss${EVENT_KEY}`;
const EVENT_MOUSEUP_DISMISS = `mouseup.dismiss${EVENT_KEY}`;
const EVENT_MOUSEDOWN_DISMISS = `mousedown.dismiss${EVENT_KEY}`;

const CLASS_NAME_SCROLLBAR_MEASURER = 'lta-modal-scrollbar-measure';
const CLASS_NAME_BACKDROP = 'lta-modal-backdrop';
const CLASS_NAME_OPEN = 'lta-modal-open';
const CLASS_NAME_FADE = 'fade';
const CLASS_NAME_SHOW = 'show';
const CLASS_NAME_ELEVATED = 'is-elevated';
const CLASS_NAME_STATIC = 'lta-modal-static';
const CLASS_NAME_FULLSCREEN = 'lta-modal-open-fullscreen';
const CLASS_DIALOG_FULLSCREEN = 'lta-modal-fullscreen';

const SELECTOR_DIALOG = '.lta-modal-dialog';
const SELECTOR_MODAL_BODY = '.lta-modal-body';
const SELECTOR_DATA_TOGGLE = '[data-toggle="modal"]';
const SELECTOR_DATA_DISMISS = '[data-dismiss="modal"]';

const NAME = 'Modal';

class Modal extends BaseComponent {
	constructor(element, config) {
		super(element);
		if (!element) {
			return;
		}

		this._config = {
			...defaultConfig,
			...config
		};
		this._element = element;
		this._dialog = SelectorEngine.findOne(SELECTOR_DIALOG, element);
		this._backdrop = null;
		this._isShown = false;
		this._isBodyOverflowing = false;
		this._ignoreBackdropClick = false;
		this._isTransitioning = false;
		this._scrollbarWidth = 0;
		this.isEscapeEventEnable = true;
		this._isFullScreen = this._dialog.classList.contains(CLASS_DIALOG_FULLSCREEN);
	}

	get presentForm() {
		return this._presentForm;
	}

	set presentForm(presentForm) {
		this._presentForm = presentForm;
	}

	static get NAME() {
		return NAME;
	}

	toggle(relatedTarget) {
		return this._isShown ? this.hide() : this.show(relatedTarget);
	}

	show(relatedTarget) {
		if (this._isShown || this._isTransitioning) {
			return;
		}

		if (this._element.classList.contains(CLASS_NAME_FADE)) {
			this._isTransitioning = true;
		}

		const showEvent = EventHandler.trigger(this._element, EVENT_SHOW, {
			relatedTarget
		});

		if (this._isShown || showEvent.defaultPrevented) {
			return;
		}

		this._isShown = true;

		this._checkScrollbar();
		this._setScrollbar();

		this._adjustDialog();
		if(this.isEscapeEventEnable) {
			this._setEscapeEvent();
		}
		this._setResizeEvent();

		EventHandler.on(this._element, EVENT_CLICK_DISMISS, SELECTOR_DATA_DISMISS, (event) => this.hide(event));

		EventHandler.on(this._dialog, EVENT_MOUSEDOWN_DISMISS, () => {
			EventHandler.one(this._element, EVENT_MOUSEUP_DISMISS, (event) => {
				if (event.target === this._element) {
					this._ignoreBackdropClick = true;
				}
			});
		});

		this._showBackdrop(() => this._showElement(relatedTarget));
	}

	hide(event) {
		if (event) {
			event.preventDefault();
		}

		if (!this._isShown || this._isTransitioning) {
			return;
		}

		const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE);

		if (hideEvent.defaultPrevented) {
			return;
		}

		this._isShown = false;
		const transition = this._element.classList.contains(CLASS_NAME_FADE);

		if (transition) {
			this._isTransitioning = true;
		}

		if(this.isEscapeEventEnable) {
			this._setEscapeEvent();
		}
		this._setResizeEvent();

		EventHandler.off(document, EVENT_FOCUSIN);

		this._element.classList.remove(CLASS_NAME_SHOW);

		EventHandler.off(this._element, EVENT_CLICK_DISMISS);
		EventHandler.off(this._dialog, EVENT_MOUSEDOWN_DISMISS);

		if (transition) {
			const transitionDuration = getTransitionDurationFromElement(this._element);

			EventHandler.one(this._element, TRANSITION_END, (event) => this._hideModal(event));
			emulateTransitionEnd(this._element, transitionDuration);
		} else {
			this._hideModal();
		}
	}

	dispose() {
		[window, this._element, this._dialog].forEach((htmlElement) => EventHandler.off(htmlElement, EVENT_KEY));

		EventHandler.off(document, EVENT_FOCUSIN);

		this._element = null;
		this._dialog = null;
		this._backdrop = null;
		this._isShown = null;
		this._isBodyOverflowing = null;
		this._ignoreBackdropClick = null;
		this._isTransitioning = null;
		this._scrollbarWidth = null;
	}

	handleUpdate() {
		this._adjustDialog();
	}

	_showElement(relatedTarget) {
		const transition = this._element.classList.contains(CLASS_NAME_FADE);
		const modalBody = SelectorEngine.findOne(SELECTOR_MODAL_BODY, this._dialog);

		if (!this._element.parentNode || this._element.parentNode.nodeType !== Node.ELEMENT_NODE) {
			// Don't move modal's DOM position
			document.body.appendChild(this._element);
		}

		this._element.style.display = 'block';

		if (!this._isFullScreen && document.body.classList.contains(CLASS_NAME_FULLSCREEN)) {
			this._element.classList.add(CLASS_NAME_ELEVATED);
		}

		this._element.removeAttribute('aria-hidden');
		this._element.setAttribute('aria-modal', true);
		this._element.setAttribute('role', 'dialog');
		this._element.scrollTop = 0;

		if (modalBody) {
			modalBody.scrollTop = 0;
		}

		if (transition) {
			reflow(this._element);
		}

		this._element.classList.add(CLASS_NAME_SHOW);

		if (this._config.focus) {
			this._enforceFocus();
		}

		const transitionComplete = () => {
			if (this._config.focus) {
				this._element.focus();
			}

			this._isTransitioning = false;
			EventHandler.trigger(this._element, EVENT_SHOWN, {
				relatedTarget
			});
		};

		if (transition) {
			const transitionDuration = getTransitionDurationFromElement(this._dialog);

			EventHandler.one(this._dialog, TRANSITION_END, transitionComplete);
			emulateTransitionEnd(this._dialog, transitionDuration);
		} else {
			transitionComplete();
		}
	}

	_enforceFocus() {
		EventHandler.off(document, EVENT_FOCUSIN); // guard against infinite focus loop
		EventHandler.on(document, EVENT_FOCUSIN, (event) => {
			if (document !== event.target && this._element !== event.target && !this._element.contains(event.target)) {
				this._element.focus();
			}
		});
	}

	_setEscapeEvent() {
		if (this._isShown) {
			EventHandler.on(this._element, EVENT_KEYDOWN_DISMISS, (event) => {
				if (this._config.keyboard && event.key === ESCAPE_KEY) {
					event.preventDefault();
					this.hide();
				} else if (!this._config.keyboard && event.key === ESCAPE_KEY) {
					this._triggerBackdropTransition();
				}
			});
		} else {
			EventHandler.off(this._element, EVENT_KEYDOWN_DISMISS);
		}
	}

	_setResizeEvent() {
		if (this._isShown) {
			EventHandler.on(window, EVENT_RESIZE, () => this._adjustDialog());
		} else {
			EventHandler.off(window, EVENT_RESIZE);
		}
	}

	_hideModal() {
		this._element.style.display = 'none';
		this._element.setAttribute('aria-hidden', true);
		this._element.removeAttribute('aria-modal');
		this._element.removeAttribute('role');
		this._isTransitioning = false;
		this._showBackdrop(() => {
			document.body.classList.remove(...[CLASS_NAME_OPEN, ...(this._isFullScreen ? [CLASS_NAME_FULLSCREEN]:[])]);
			this._resetAdjustments();
			this._resetScrollbar();
			EventHandler.trigger(this._element, EVENT_HIDDEN);
		});
	}

	_removeBackdrop() {
		this._backdrop.parentNode.removeChild(this._backdrop);
		this._backdrop = null;
	}

	_showBackdrop(callback) {
		const animate = this._element.classList.contains(CLASS_NAME_FADE) ? CLASS_NAME_FADE : '';

		if (this._isShown && this._config.backdrop) {
			this._backdrop = document.createElement('div');
			this._backdrop.className = CLASS_NAME_BACKDROP;

			if (animate) {
				this._backdrop.classList.add(animate);
			}

			if (!this._isFullScreen && document.body.classList.contains(CLASS_NAME_FULLSCREEN)) {
				this._backdrop.classList.add(CLASS_NAME_ELEVATED);
			}

			this._element.parentNode.insertBefore(this._backdrop, this._element);

			EventHandler.on(this._element, EVENT_CLICK_DISMISS, (event) => {
				if (this._ignoreBackdropClick) {
					this._ignoreBackdropClick = false;
					return;
				}

				if (event.target !== event.currentTarget) {
					return;
				}

				this._triggerBackdropTransition();
			});

			if (animate) {
				reflow(this._backdrop);
			}

			this._backdrop.classList.add(CLASS_NAME_SHOW);

			if (!animate) {
				callback();
				return;
			}

			const backdropTransitionDuration = getTransitionDurationFromElement(this._backdrop);

			EventHandler.one(this._backdrop, TRANSITION_END, callback);
			emulateTransitionEnd(this._backdrop, backdropTransitionDuration);
		} else if (!this._isShown && this._backdrop) {
			this._backdrop.classList.remove(CLASS_NAME_SHOW);

			const callbackRemove = () => {
				this._removeBackdrop();
				callback();
			};

			if (this._element.classList.contains(CLASS_NAME_FADE)) {
				const backdropTransitionDuration = getTransitionDurationFromElement(this._backdrop);
				EventHandler.one(this._backdrop, TRANSITION_END, callbackRemove);
				emulateTransitionEnd(this._backdrop, backdropTransitionDuration);
			} else {
				callbackRemove();
			}
		} else {
			callback();
		}
	}

	_triggerBackdropTransition() {
		if (this._config.backdrop === 'static') {
			const hideEvent = EventHandler.trigger(this._element, EVENT_HIDE_PREVENTED);
			if (hideEvent.defaultPrevented) {
				return;
			}

			const isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight;

			if (!isModalOverflowing) {
				this._element.style.overflowY = 'hidden';
			}

			this._element.classList.add(CLASS_NAME_STATIC);
			const modalTransitionDuration = getTransitionDurationFromElement(this._dialog);
			EventHandler.off(this._element, TRANSITION_END);
			EventHandler.one(this._element, TRANSITION_END, () => {
				this._element.classList.remove(CLASS_NAME_STATIC);
				if (!isModalOverflowing) {
					EventHandler.one(this._element, TRANSITION_END, () => {
						this._element.style.overflowY = '';
					});
					emulateTransitionEnd(this._element, modalTransitionDuration);
				}
			});
			emulateTransitionEnd(this._element, modalTransitionDuration);
			this._element.focus();
		} else {
			this.hide();
		}
	}

	// The following methods are used to handle overflowing modals
	_adjustDialog() {
		const isModalOverflowing = this._element.scrollHeight > document.documentElement.clientHeight;

		if (!this._isBodyOverflowing && isModalOverflowing) {
			this._element.style.paddingLeft = `${this._scrollbarWidth}px`;
		}

		if (this._isBodyOverflowing && !isModalOverflowing) {
			this._element.style.paddingRight = `${this._scrollbarWidth}px`;
		}
	}

	_resetAdjustments() {
		this._element.style.paddingLeft = '';
		this._element.style.paddingRight = '';
	}

	_checkScrollbar() {
		const rect = document.body.getBoundingClientRect();
		this._isBodyOverflowing = Math.round(rect.left + rect.right) < window.innerWidth;
		this._scrollbarWidth = this._getScrollbarWidth();
	}

	_setScrollbar() {
		if (this._isBodyOverflowing) {
			// Adjust body padding
			const actualPadding = document.body.style.paddingRight;
			const calculatedPadding = window.getComputedStyle(document.body)['padding-right'];

			Manipulator.setDataAttribute(document.body, 'padding-right', actualPadding);
			document.body.style.paddingRight = `${Number.parseFloat(calculatedPadding) + this._scrollbarWidth}px`;
		}

		document.body.classList.add(...[CLASS_NAME_OPEN, ...(this._isFullScreen ? [CLASS_NAME_FULLSCREEN]:[])]);
	}

	_resetScrollbar() {
		// Restore body padding
		const padding = Manipulator.getDataAttribute(document.body, 'padding-right');
		if (typeof padding === 'undefined') {
			document.body.style.paddingRight = '';
		} else {
			Manipulator.removeDataAttribute(document.body, 'padding-right');
			document.body.style.paddingRight = padding;
		}
	}

	_getScrollbarWidth() {
		const scrollDiv = document.createElement('div');
		scrollDiv.className = CLASS_NAME_SCROLLBAR_MEASURER;
		// eslint-disable-next-line unicorn/prefer-node-append
		document.body.appendChild(scrollDiv);

		const scrollbarWidth = scrollDiv.getBoundingClientRect().width - scrollDiv.clientWidth;
		// eslint-disable-next-line unicorn/prefer-node-remove
		document.body.removeChild(scrollDiv);

		return scrollbarWidth;
	}
}

export default Modal;
